(() => {
  'use strict';

  const MAIN_STORAGE_KEY =
    'zlnDesignLaborStateV1';

  const STRUCTURE_STORAGE_KEY =
    'zlnDesignLaborStructureStateV1';

  const INITIALIZED_ATTRIBUTE =
    'data-zln-structure-labor-initialized';

  const query = (
    root,
    selector
  ) => {
    return (
      root?.querySelector(
        selector
      ) || null
    );
  };

  const queryAll = (
    root,
    selector
  ) => {
    return Array.from(
      root?.querySelectorAll(
        selector
      ) || []
    );
  };

  const deepClone = (
    value
  ) => {
    return JSON.parse(
      JSON.stringify(
        value
      )
    );
  };

  const clamp = (
    value,
    minimum,
    maximum
  ) => {
    return Math.max(
      minimum,
      Math.min(
        maximum,
        value
      )
    );
  };

  const toInteger = (
    value,
    fallback = 0
  ) => {
    const parsed =
      Number.parseInt(
        String(value),
        10
      );

    return Number.isFinite(
      parsed
    )
      ? parsed
      : fallback;
  };

  const normalizeText = (
    value,
    maximumLength
  ) => {
    return String(
      value ?? ''
    )
      .trim()
      .slice(
        0,
        maximumLength
      );
  };

  function readStorage(
    key,
    fallback = {}
  ) {
    try {
      const raw =
        window.localStorage.getItem(
          key
        );

      return raw
        ? JSON.parse(raw)
        : fallback;
    } catch (error) {
      return fallback;
    }
  }

  function writeStorage(
    key,
    value
  ) {
    try {
      window.localStorage.setItem(
        key,
        JSON.stringify(
          value
        )
      );

      return true;
    } catch (error) {
      return false;
    }
  }

  function escapeHtml(
    value
  ) {
    return String(
      value ?? ''
    )
      .replaceAll(
        '&',
        '&amp;'
      )
      .replaceAll(
        '<',
        '&lt;'
      )
      .replaceAll(
        '>',
        '&gt;'
      )
      .replaceAll(
        '"',
        '&quot;'
      )
      .replaceAll(
        "'",
        '&#039;'
      );
  }

  function formatNumber(
    value
  ) {
    return new Intl.NumberFormat(
      'hu-HU',
      {
        maximumFractionDigits:
          0
      }
    ).format(
      Math.max(
        0,
        Number(value) || 0
      )
    );
  }

  function pluralizePages(
    value
  ) {
    return `${formatNumber(
      value
    )} oldal`;
  }

  function pluralizeProducts(
    value
  ) {
    return `${formatNumber(
      value
    )} termék`;
  }

  function createCustomPageId() {
    if (
      window.crypto &&
      typeof window.crypto.randomUUID ===
        'function'
    ) {
      return window.crypto.randomUUID();
    }

    return [
      'custom-page',
      Date.now(),
      Math.random()
        .toString(16)
        .slice(2)
    ].join('-');
  }

  function getCurrentScript() {
    return (
      document.currentScript ||
      Array.from(
        document.scripts
      ).find(
        (script) => {
          return script.src.includes(
            'zln-labor-structure.js'
          );
        }
      ) ||
      null
    );
  }

  function ensureDataFile() {
    if (
      window
        .ZLN_LABOR_STRUCTURE_DATA
    ) {
      initializeAll();
      return;
    }

    const existingScript =
      Array.from(
        document.scripts
      ).find(
        (script) => {
          return script.src.includes(
            'zln-labor-structure-data.js'
          );
        }
      );

    if (existingScript) {
      existingScript.addEventListener(
        'load',
        initializeAll,
        {
          once: true
        }
      );

      document.addEventListener(
        'zln:labor-structure-data-ready',
        initializeAll,
        {
          once: true
        }
      );

      return;
    }

    const currentScript =
      getCurrentScript();

    if (
      !currentScript?.src
    ) {
      document.addEventListener(
        'zln:labor-structure-data-ready',
        initializeAll,
        {
          once: true
        }
      );

      return;
    }

    const dataScript =
      document.createElement(
        'script'
      );

    dataScript.src =
      currentScript.src.replace(
        'zln-labor-structure.js',
        'zln-labor-structure-data.js'
      );

    dataScript.async =
      false;

    dataScript.addEventListener(
      'load',
      initializeAll,
      {
        once: true
      }
    );

    dataScript.addEventListener(
      'error',
      () => {
        console.error(
          '[ZLN Design Labor] A Struktúralabor adatfájlja nem tölthető be.'
        );
      }
    );

    document.head.appendChild(
      dataScript
    );
  }

  function createDefaultState(
    data
  ) {
    const structure =
      deepClone(
        data.defaultStructure
      );

    return {
      version:
        data.version || 2,

      structureMode:
        structure
          .structureMode ||
        'recommended',

      pageQuantities:
        structure
          .pageQuantities ||
        {},

      customPages:
        Array.isArray(
          structure.customPages
        )
          ? structure.customPages
          : [],

      catalogDepthId:
        structure
          .catalogDepthId,

      catalog:
        structure.catalog ||
        {},

      productCount:
        structure
          .productCount ||
        0,

      uploadService:
        Boolean(
          structure
            .uploadService
        ),

      notes:
        String(
          structure.notes ||
          ''
        ),

      updatedAt:
        null
    };
  }

  function normalizeCustomPages(
    source,
    data
  ) {
    const settings =
      data.customPageOptions ||
      {};

    const maximumPages =
      toInteger(
        settings.maximumPages,
        30
      );

    const minimumLength =
      toInteger(
        settings.name
          ?.minimumLength,
        2
      );

    const maximumNameLength =
      toInteger(
        settings.name
          ?.maximumLength,
        80
      );

    const maximumDescriptionLength =
      toInteger(
        settings.description
          ?.maximumLength,
        240
      );

    const usedNames =
      new Set();

    return (
      Array.isArray(source)
        ? source
        : []
    )
      .map(
        (item) => {
          const sourceObject =
            typeof item ===
            'string'
              ? {
                  name: item,
                  description:
                    ''
                }
              : (
                  item || {}
                );

          const name =
            normalizeText(
              sourceObject.name,
              maximumNameLength
            );

          const description =
            normalizeText(
              sourceObject
                .description,
              maximumDescriptionLength
            );

          if (
            name.length <
            minimumLength
          ) {
            return null;
          }

          const nameKey =
            name.toLocaleLowerCase(
              'hu-HU'
            );

          if (
            settings
              .duplicateNamesAllowed ===
              false &&
            usedNames.has(
              nameKey
            )
          ) {
            return null;
          }

          usedNames.add(
            nameKey
          );

          return {
            id:
              String(
                sourceObject.id ||
                createCustomPageId()
              ),

            name,

            description,

            createdAt:
              sourceObject
                .createdAt ||
              null,

            updatedAt:
              sourceObject
                .updatedAt ||
              null
          };
        }
      )
      .filter(Boolean)
      .slice(
        0,
        maximumPages
      );
  }

  function normalizeState(
    source,
    data
  ) {
    const defaults =
      createDefaultState(
        data
      );

    const normalizedPages = {
      ...defaults
        .pageQuantities,

      ...(
        source
          .pageQuantities ||
        {}
      )
    };

    const normalizedCatalog = {
      ...defaults.catalog,

      ...(
        source.catalog ||
        {}
      )
    };

    data.pageTypeOptions.forEach(
      (option) => {
        normalizedPages[
          option.id
        ] =
          clamp(
            toInteger(
              normalizedPages[
                option.id
              ],
              option
                .defaultQuantity
            ),
            option.minimum,
            option.maximum
          );
      }
    );

    data.catalogFields.forEach(
      (field) => {
        normalizedCatalog[
          field.id
        ] =
          clamp(
            toInteger(
              normalizedCatalog[
                field.id
              ],
              field.defaultValue
            ),
            field.minimum,
            field.maximum
          );
      }
    );

    const validDepth =
      data
        .catalogDepthOptions
        .some(
          (option) => {
            return (
              option.id ===
              source
                .catalogDepthId
            );
          }
        );

    const uploadService =
      Boolean(
        source
          .uploadService
      );

    const shouldResetProductCount =
      data.productOptions
        ?.productCount
        ?.resetWhenUploadDisabled !==
        false;

    const normalizedProductCount =
      uploadService
        ? clamp(
            toInteger(
              source
                .productCount,
              defaults
                .productCount
            ),
            data
              .productOptions
              .productCount
              .minimum,
            data
              .productOptions
              .productCount
              .maximum
          )
        : (
            shouldResetProductCount
              ? 0
              : clamp(
                  toInteger(
                    source
                      .productCount,
                    defaults
                      .productCount
                  ),
                  data
                    .productOptions
                    .productCount
                    .minimum,
                  data
                    .productOptions
                    .productCount
                    .maximum
                )
          );

    return {
      ...defaults,
      ...source,

      version:
        data.version || 2,

      structureMode:
        source
          .structureMode ||
        defaults
          .structureMode ||
        'recommended',

      pageQuantities:
        normalizedPages,

      customPages:
        normalizeCustomPages(
          source.customPages ??
          defaults.customPages,
          data
        ),

      catalog:
        normalizedCatalog,

      catalogDepthId:
        validDepth
          ? source
              .catalogDepthId
          : defaults
              .catalogDepthId,

      productCount:
        normalizedProductCount,

      uploadService,

      notes:
        String(
          source.notes ||
          ''
        ).slice(
          0,
          2000
        )
    };
  }

  function loadState(
    data
  ) {
    const separateState =
      readStorage(
        STRUCTURE_STORAGE_KEY,
        {}
      );

    const mainState =
      readStorage(
        MAIN_STORAGE_KEY,
        {}
      );

    const mainStructureState =
      mainState.structure ||
      mainState
        .structureSelection ||
      {};

    return normalizeState(
      {
        ...mainStructureState,
        ...separateState,

        pageQuantities: {
          ...(
            mainStructureState
              .pageQuantities ||
            {}
          ),

          ...(
            separateState
              .pageQuantities ||
            {}
          )
        },

        customPages:
          separateState
            .customPages ??
          mainStructureState
            .customPages ??
          [],

        catalog: {
          ...(
            mainStructureState
              .catalog ||
            {}
          ),

          ...(
            separateState
              .catalog ||
            {}
          )
        }
      },
      data
    );
  }

  function calculateStructure(
    state,
    data
  ) {
    const standardPages =
      Object.values(
        state.pageQuantities
      ).reduce(
        (
          total,
          value
        ) => {
          return (
            total +
            Math.max(
              0,
              toInteger(
                value,
                0
              )
            )
          );
        },
        0
      );

    const customPages =
      Array.isArray(
        state.customPages
      )
        ? state
            .customPages
            .length
        : 0;

    const uniquePages =
      standardPages +
      customPages;

    const depth =
      data
        .catalogDepthOptions
        .find(
          (option) => {
            return (
              option.id ===
              state
                .catalogDepthId
            );
          }
        ) ||
      data
        .catalogDepthOptions[
        0
      ];

    const productCount =
      state.uploadService
        ? Math.max(
            0,
            toInteger(
              state
                .productCount,
              0
            )
          )
        : 0;

    return {
      standardPages,
      customPages,
      uniquePages,
      depth,
      productCount,

      dynamicProductPages:
        productCount,

      uploadRequested:
        Boolean(
          state
            .uploadService
        )
    };
  }

  function saveState(
    state,
    data
  ) {
    state.updatedAt =
      new Date()
        .toISOString();

    const calculations =
      calculateStructure(
        state,
        data
      );

    writeStorage(
      STRUCTURE_STORAGE_KEY,
      state
    );

    const latestMainState =
      readStorage(
        MAIN_STORAGE_KEY,
        {}
      );

    const structureState = {
      version:
        state.version,

      structureMode:
        state.structureMode,

      pageQuantities:
        deepClone(
          state
            .pageQuantities
        ),

      customPages:
        deepClone(
          state
            .customPages
        ),

      catalogDepthId:
        state
          .catalogDepthId,

      catalog:
        deepClone(
          state.catalog
        ),

      productCount:
        state
          .productCount,

      uploadService:
        state
          .uploadService,

      notes:
        state.notes,

      calculations:
        deepClone(
          calculations
        ),

      updatedAt:
        state
          .updatedAt
    };

    writeStorage(
      MAIN_STORAGE_KEY,
      {
        ...latestMainState,

        structure:
          structureState,

        structureSelection:
          structureState,

        updatedAt:
          state.updatedAt
      }
    );
  }

  function initializeLabor(
    stage,
    data
  ) {
    if (
      !stage ||
      stage.getAttribute(
        INITIALIZED_ATTRIBUTE
      ) === 'true'
    ) {
      return;
    }

    stage.setAttribute(
      INITIALIZED_ATTRIBUTE,
      'true'
    );

    const section =
      stage.closest(
        '[data-zln-design-labor]'
      ) ||
      stage.closest(
        '.zln-design-labor-section'
      ) ||
      stage.parentElement;

    const state =
      loadState(
        data
      );

    let saveTimer =
      null;

    const elements = {
      standardPageCount:
        query(
          stage,
          '[data-structure-standard-page-count]'
        ),

      customPageCountSummary:
        query(
          stage,
          '[data-structure-custom-page-count-summary]'
        ),

      uniquePages:
        query(
          stage,
          '[data-structure-unique-pages]'
        ),

      catalogDepthName:
        query(
          stage,
          '[data-structure-catalog-depth-name]'
        ),

      productCountSummary:
        query(
          stage,
          '[data-structure-product-count]'
        ),

      uploadState:
        query(
          stage,
          '[data-structure-upload-state]'
        ),

      progressItems:
        queryAll(
          stage,
          '[data-structure-progress-item]'
        ),

      resetRecommended:
        query(
          stage,
          '[data-structure-reset-recommended]'
        ),

      pageGroups:
        query(
          stage,
          '[data-structure-page-groups]'
        ),

      standardPageTotal:
        query(
          stage,
          '[data-structure-standard-page-total]'
        ),

      pageTotal:
        query(
          stage,
          '[data-structure-page-total]'
        ),

      customPageName:
        query(
          stage,
          '[data-structure-custom-page-name]'
        ),

      customPageDescription:
        query(
          stage,
          '[data-structure-custom-page-description]'
        ),

      customPageAdd:
        query(
          stage,
          '[data-structure-custom-page-add]'
        ),

      customPageMessage:
        query(
          stage,
          '[data-structure-custom-page-message]'
        ),

      customPageList:
        query(
          stage,
          '[data-structure-custom-page-list]'
        ),

      customPageEmpty:
        query(
          stage,
          '[data-structure-custom-page-empty]'
        ),

      customPageCount:
        query(
          stage,
          '[data-structure-custom-page-count]'
        ),

      depthGrid:
        query(
          stage,
          '[data-structure-depth-grid]'
        ),

      catalogPath:
        query(
          stage,
          '[data-structure-catalog-path]'
        ),

      catalogPathName:
        query(
          stage,
          '[data-structure-catalog-path-name]'
        ),

      catalogPathSteps:
        query(
          stage,
          '[data-structure-catalog-path-steps]'
        ),

      catalogFields:
        query(
          stage,
          '[data-structure-catalog-fields]'
        ),

      catalogFieldGrid:
        query(
          stage,
          '[data-structure-catalog-field-grid]'
        ),

      productCountCard:
        query(
          stage,
          '[data-structure-product-count-card]'
        ),

      productDecrease:
        query(
          stage,
          '[data-structure-product-decrease]'
        ),

      productIncrease:
        query(
          stage,
          '[data-structure-product-increase]'
        ),

      productInput:
        query(
          stage,
          '[data-structure-product-input]'
        ),

      dynamicProductPages:
        query(
          stage,
          '[data-structure-dynamic-product-pages]'
        ),

      uploadInput:
        query(
          stage,
          '[data-structure-upload-input]'
        ),

      uploadLabel:
        query(
          stage,
          '[data-structure-upload-label]'
        ),

      uploadNotice:
        query(
          stage,
          '[data-structure-upload-notice]'
        ),

      notes:
        query(
          stage,
          '[data-structure-notes]'
        ),

      notesCount:
        query(
          stage,
          '[data-structure-notes-count]'
        ),

      backButton:
        query(
          stage,
          '[data-structure-back]'
        ),

      nextButton:
        query(
          stage,
          '[data-structure-next]'
        ),

      status:
        query(
          stage,
          '[data-structure-status]'
        ),

      statusDot:
        query(
          stage,
          '[data-structure-status-dot]'
        )
    };

    function getDepth() {
      return (
        data
          .catalogDepthOptions
          .find(
            (option) => {
              return (
                option.id ===
                state
                  .catalogDepthId
              );
            }
          ) ||
        data
          .catalogDepthOptions[
          0
        ]
      );
    }

    function setProgress(
      activeId
    ) {
      const order = [
        'pages',
        'custom',
        'catalog',
        'products'
      ];

      const activeIndex =
        Math.max(
          0,
          order.indexOf(
            activeId
          )
        );

      elements
        .progressItems
        .forEach(
          (item) => {
            const itemIndex =
              order.indexOf(
                item.dataset
                  .structureProgressItem
              );

            item.classList.toggle(
              'is-active',
              itemIndex ===
                activeIndex
            );

            item.classList.toggle(
              'is-complete',
              itemIndex <
                activeIndex
            );
          }
        );
    }

    function dispatchStructureUpdate() {
      document.dispatchEvent(
        new CustomEvent(
          'zln:labor-structure-updated',
          {
            detail: {
              structure:
                deepClone(
                  state
                ),

              calculations:
                calculateStructure(
                  state,
                  data
                )
            }
          }
        )
      );
    }

    function commit({
      rerenderPages = false,
      rerenderCustomPages = false,
      rerenderDepth = false,
      rerenderCatalogFields = false,
      updateStatus = true
    } = {}) {
      if (
        rerenderPages
      ) {
        renderPageGroups();
      }

      if (
        rerenderCustomPages
      ) {
        renderCustomPages();
      }

      if (
        rerenderDepth
      ) {
        renderDepthOptions();
      }

      if (
        rerenderCatalogFields
      ) {
        renderCatalogDetails();
      }

      renderProductControls();
      renderSummary();

      if (
        updateStatus
      ) {
        renderStatus();
      }

      saveState(
        state,
        data
      );

      dispatchStructureUpdate();
    }

    function scheduleNotesSave() {
      window.clearTimeout(
        saveTimer
      );

      saveTimer =
        window.setTimeout(
          () => {
            saveState(
              state,
              data
            );

            dispatchStructureUpdate();
          },
          180
        );
    }

    function setCustomPageMessage(
      message,
      stateName = 'neutral'
    ) {
      if (
        !elements
          .customPageMessage
      ) {
        return;
      }

      elements
        .customPageMessage
        .textContent =
          message;

      elements
        .customPageMessage
        .dataset.state =
          stateName;
    }

    function createNumberControl({
      value,
      minimum,
      maximum,
      disabled = false,
      label,
      onChange
    }) {
      const wrapper =
        document.createElement(
          'div'
        );

      wrapper.className =
        'zln-structure-page-card__control';

      const decrease =
        document.createElement(
          'button'
        );

      decrease.type =
        'button';

      decrease.className =
        'zln-structure-page-card__button';

      decrease.textContent =
        '−';

      decrease.setAttribute(
        'aria-label',
        `${label} csökkentése`
      );

      const input =
        document.createElement(
          'input'
        );

      input.className =
        'zln-structure-page-card__input';

      input.type =
        'number';

      input.min =
        String(
          minimum
        );

      input.max =
        String(
          maximum
        );

      input.step =
        '1';

      input.value =
        String(
          value
        );

      input.inputMode =
        'numeric';

      input.setAttribute(
        'aria-label',
        label
      );

      const increase =
        document.createElement(
          'button'
        );

      increase.type =
        'button';

      increase.className =
        'zln-structure-page-card__button';

      increase.textContent =
        '+';

      increase.setAttribute(
        'aria-label',
        `${label} növelése`
      );

      const updateDisabledState =
        (
          currentValue
        ) => {
          decrease.disabled =
            disabled ||
            currentValue <=
              minimum;

          increase.disabled =
            disabled ||
            currentValue >=
              maximum;

          input.disabled =
            disabled;
        };

      const applyValue =
        (
          nextValue
        ) => {
          const normalized =
            clamp(
              toInteger(
                nextValue,
                minimum
              ),
              minimum,
              maximum
            );

          input.value =
            String(
              normalized
            );

          updateDisabledState(
            normalized
          );

          onChange(
            normalized
          );
        };

      decrease.addEventListener(
        'click',
        () => {
          applyValue(
            toInteger(
              input.value,
              value
            ) - 1
          );
        }
      );

      increase.addEventListener(
        'click',
        () => {
          applyValue(
            toInteger(
              input.value,
              value
            ) + 1
          );
        }
      );

      input.addEventListener(
        'input',
        () => {
          const normalized =
            clamp(
              toInteger(
                input.value,
                minimum
              ),
              minimum,
              maximum
            );

          updateDisabledState(
            normalized
          );

          onChange(
            normalized
          );
        }
      );

      input.addEventListener(
        'blur',
        () => {
          applyValue(
            input.value
          );
        }
      );

      updateDisabledState(
        value
      );

      wrapper.append(
        decrease,
        input,
        increase
      );

      return wrapper;
    }

    function renderPageGroups() {
      if (
        !elements.pageGroups
      ) {
        return;
      }

      elements
        .pageGroups
        .innerHTML =
          '';

      data.pageGroups.forEach(
        (group) => {
          const options =
            data
              .pageTypeOptions
              .filter(
                (option) => {
                  return (
                    option.group ===
                    group.id
                  );
                }
              );

          if (
            !options.length
          ) {
            return;
          }

          const groupQuantity =
            options.reduce(
              (
                total,
                option
              ) => {
                return (
                  total +
                  state
                    .pageQuantities[
                    option.id
                  ]
                );
              },
              0
            );

          const groupSection =
            document.createElement(
              'section'
            );

          groupSection.className =
            `zln-structure-page-group${
              group.recommended
                ? ' is-recommended'
                : ''
            }`;

          groupSection.innerHTML = `
            <header class="zln-structure-page-group__header">
              <div>
                <div class="zln-structure-page-group__title-row">
                  <h4 class="zln-structure-page-group__title">
                    ${escapeHtml(
                      group.name
                    )}
                  </h4>

                  ${
                    group.recommended
                      ? `
                        <span class="zln-structure-page-group__recommended">
                          Ajánlott
                        </span>
                      `
                      : ''
                  }
                </div>

                <p class="zln-structure-page-group__description">
                  ${escapeHtml(
                    group.description
                  )}
                </p>
              </div>

              <span class="zln-structure-page-group__count">
                ${pluralizePages(
                  groupQuantity
                )}
              </span>
            </header>

            <div class="zln-structure-page-group__grid"></div>
          `;

          const grid =
            query(
              groupSection,
              '.zln-structure-page-group__grid'
            );

          options.forEach(
            (option) => {
              const quantity =
                state
                  .pageQuantities[
                  option.id
                ];

              const card =
                document.createElement(
                  'article'
                );

              card.className =
                `zln-structure-page-card${
                  quantity > 0
                    ? ' is-active'
                    : ''
                }${
                  option.required
                    ? ' is-required'
                    : ''
                }${
                  option.recommended
                    ? ' is-recommended'
                    : ''
                }`;

              const copy =
                document.createElement(
                  'div'
                );

              copy.className =
                'zln-structure-page-card__copy';

              copy.innerHTML = `
                <div class="zln-structure-page-card__meta">
                  <p class="zln-structure-page-card__name">
                    ${escapeHtml(
                      option.name
                    )}
                  </p>

                  ${
                    option.required
                      ? `
                        <span class="zln-structure-page-card__required">
                          Kötelező
                        </span>
                      `
                      : ''
                  }

                  ${
                    option.recommended &&
                    !option.required
                      ? `
                        <span class="zln-structure-page-card__recommended">
                          Ajánlott
                        </span>
                      `
                      : ''
                  }
                </div>

                <p class="zln-structure-page-card__description">
                  ${escapeHtml(
                    option.description
                  )}
                </p>
              `;

              const control =
                createNumberControl({
                  value:
                    quantity,

                  minimum:
                    option.minimum,

                  maximum:
                    option.maximum,

                  disabled:
                    Boolean(
                      option.locked
                    ) ||
                    (
                      option.required &&
                      option.minimum ===
                        option.maximum
                    ),

                  label:
                    `${option.name} darabszáma`,

                  onChange:
                    (
                      nextValue
                    ) => {
                      state
                        .pageQuantities[
                        option.id
                      ] =
                        nextValue;

                      state
                        .structureMode =
                          'customized';

                      setProgress(
                        'pages'
                      );

                      commit({
                        rerenderPages:
                          true,

                        updateStatus:
                          true
                      });
                    }
                });

              card.append(
                copy,
                control
              );

              grid.appendChild(
                card
              );
            }
          );

          elements
            .pageGroups
            .appendChild(
              groupSection
            );
        }
      );
    }

    function validateCustomPage(
      name,
      currentId = null
    ) {
      const settings =
        data
          .customPageOptions ||
        {};

      const minimumLength =
        toInteger(
          settings.name
            ?.minimumLength,
          2
        );

      const maximumLength =
        toInteger(
          settings.name
            ?.maximumLength,
          80
        );

      const normalizedName =
        normalizeText(
          name,
          maximumLength
        );

      if (
        normalizedName.length <
        minimumLength
      ) {
        return {
          valid: false,

          message:
            `Az oldal neve legalább ${minimumLength} karakter legyen.`,

          name:
            normalizedName
        };
      }

      const duplicate =
        settings
          .duplicateNamesAllowed ===
          false &&
        state
          .customPages
          .some(
            (page) => {
              return (
                page.id !==
                  currentId &&
                page.name
                  .toLocaleLowerCase(
                    'hu-HU'
                  ) ===
                  normalizedName
                    .toLocaleLowerCase(
                      'hu-HU'
                    )
              );
            }
          );

      if (
        duplicate
      ) {
        return {
          valid: false,

          message:
            'Ilyen nevű saját oldalt már hozzáadtál.',

          name:
            normalizedName
        };
      }

      return {
        valid: true,

        message:
          '',

        name:
          normalizedName
      };
    }

    function addCustomPage() {
      const settings =
        data
          .customPageOptions ||
        {};

      const maximumPages =
        toInteger(
          settings.maximumPages,
          30
        );

      if (
        state
          .customPages
          .length >=
        maximumPages
      ) {
        setCustomPageMessage(
          `Legfeljebb ${maximumPages} saját oldal adható hozzá.`,
          'error'
        );

        return;
      }

      const validation =
        validateCustomPage(
          elements
            .customPageName
            ?.value ||
          ''
        );

      if (
        !validation.valid
      ) {
        setCustomPageMessage(
          validation.message,
          'error'
        );

        elements
          .customPageName
          ?.focus();

        return;
      }

      const maximumDescriptionLength =
        toInteger(
          settings.description
            ?.maximumLength,
          240
        );

      const timestamp =
        new Date()
          .toISOString();

      state
        .customPages
        .push({
          id:
            createCustomPageId(),

          name:
            validation.name,

          description:
            normalizeText(
              elements
                .customPageDescription
                ?.value ||
              '',
              maximumDescriptionLength
            ),

          createdAt:
            timestamp,

          updatedAt:
            timestamp
        });

      state.structureMode =
        'customized';

      if (
        elements
          .customPageName
      ) {
        elements
          .customPageName
          .value =
            '';
      }

      if (
        elements
          .customPageDescription
      ) {
        elements
          .customPageDescription
          .value =
            '';
      }

      setCustomPageMessage(
        'A saját oldal hozzáadva és elmentve.',
        'success'
      );

      setProgress(
        'custom'
      );

      commit({
        rerenderCustomPages:
          true,

        updateStatus:
          true
      });

      elements
        .customPageName
        ?.focus();
    }

    function updateCustomPage(
      pageId,
      name,
      description
    ) {
      const page =
        state
          .customPages
          .find(
            (item) => {
              return (
                item.id ===
                pageId
              );
            }
          );

      if (
        !page
      ) {
        return;
      }

      const validation =
        validateCustomPage(
          name,
          pageId
        );

      if (
        !validation.valid
      ) {
        setCustomPageMessage(
          validation.message,
          'error'
        );

        return;
      }

      const maximumDescriptionLength =
        toInteger(
          data
            .customPageOptions
            ?.description
            ?.maximumLength,
          240
        );

      page.name =
        validation.name;

      page.description =
        normalizeText(
          description,
          maximumDescriptionLength
        );

      page.updatedAt =
        new Date()
          .toISOString();

      state.structureMode =
        'customized';

      setCustomPageMessage(
        'A saját oldal módosításai elmentve.',
        'success'
      );

      setProgress(
        'custom'
      );

      commit({
        rerenderCustomPages:
          true,

        updateStatus:
          true
      });
    }

    function removeCustomPage(
      pageId
    ) {
      const page =
        state
          .customPages
          .find(
            (item) => {
              return (
                item.id ===
                pageId
              );
            }
          );

      if (
        !page
      ) {
        return;
      }

      state.customPages =
        state
          .customPages
          .filter(
            (item) => {
              return (
                item.id !==
                pageId
              );
            }
          );

      state.structureMode =
        'customized';

      setCustomPageMessage(
        `A(z) „${page.name}” oldal törölve.`,
        'success'
      );

      setProgress(
        'custom'
      );

      commit({
        rerenderCustomPages:
          true,

        updateStatus:
          true
      });
    }

    function renderCustomPages() {
      if (
        !elements
          .customPageList
      ) {
        return;
      }

      const pages =
        state
          .customPages;

      if (
        elements
          .customPageCount
      ) {
        elements
          .customPageCount
          .textContent =
            pluralizePages(
              pages.length
            );
      }

      elements
        .customPageList
        .innerHTML =
          '';

      if (
        !pages.length
      ) {
        const empty =
          document.createElement(
            'div'
          );

        empty.className =
          'zln-structure-custom-pages__empty';

        empty.setAttribute(
          'data-structure-custom-page-empty',
          ''
        );

        empty.innerHTML = `
          <span
            class="zln-structure-custom-pages__empty-icon"
            aria-hidden="true"
          >
            +
          </span>

          <div>
            <p class="zln-structure-custom-pages__empty-title">
              Még nincs hozzáadott saját oldal.
            </p>

            <p class="zln-structure-custom-pages__empty-description">
              Az általános struktúrán felüli oldalakat a fenti
              mező segítségével adhatod hozzá.
            </p>
          </div>
        `;

        elements
          .customPageList
          .appendChild(
            empty
          );

        return;
      }

      pages.forEach(
        (
          page,
          index
        ) => {
          const card =
            document.createElement(
              'article'
            );

          card.className =
            'zln-structure-custom-page-card';

          card.dataset
            .customPageId =
              page.id;

          card.innerHTML = `
            <div class="zln-structure-custom-page-card__header">
              <div class="zln-structure-custom-page-card__index">
                ${String(
                  index + 1
                ).padStart(
                  2,
                  '0'
                )}
              </div>

              <div class="zln-structure-custom-page-card__heading">
                <p class="zln-structure-custom-page-card__eyebrow">
                  Saját oldal
                </p>

                <p class="zln-structure-custom-page-card__current-name">
                  ${escapeHtml(
                    page.name
                  )}
                </p>
              </div>
            </div>

            <div class="zln-structure-custom-page-card__fields">
              <label class="zln-structure-custom-page-field">
                <span class="zln-structure-custom-page-field__label">
                  Oldal neve
                </span>

                <input
                  class="zln-structure-custom-page-field__input"
                  type="text"
                  maxlength="80"
                  value="${escapeHtml(
                    page.name
                  )}"
                  data-custom-page-edit-name
                >
              </label>

              <label class="zln-structure-custom-page-field">
                <span class="zln-structure-custom-page-field__label">
                  Rövid leírás
                </span>

                <textarea
                  class="zln-structure-custom-page-field__textarea"
                  rows="3"
                  maxlength="240"
                  data-custom-page-edit-description
                >${escapeHtml(
                  page.description
                )}</textarea>
              </label>
            </div>

            <div class="zln-structure-custom-page-card__actions">
              <button
                class="zln-structure-custom-page-card__save"
                type="button"
                data-custom-page-save
              >
                Módosítások mentése
              </button>

              <button
                class="zln-structure-custom-page-card__delete"
                type="button"
                data-custom-page-delete
              >
                Oldal törlése
              </button>
            </div>
          `;

          const nameInput =
            query(
              card,
              '[data-custom-page-edit-name]'
            );

          const descriptionInput =
            query(
              card,
              '[data-custom-page-edit-description]'
            );

          query(
            card,
            '[data-custom-page-save]'
          )
            ?.addEventListener(
              'click',
              () => {
                updateCustomPage(
                  page.id,
                  nameInput
                    ?.value ||
                  '',
                  descriptionInput
                    ?.value ||
                  ''
                );
              }
            );

          query(
            card,
            '[data-custom-page-delete]'
          )
            ?.addEventListener(
              'click',
              () => {
                removeCustomPage(
                  page.id
                );
              }
            );

          elements
            .customPageList
            .appendChild(
              card
            );
        }
      );
    }

    function resetRecommendedStructure() {
      const defaults =
        createDefaultState(
          data
        );

      state.pageQuantities =
        deepClone(
          defaults
            .pageQuantities
        );

      state.structureMode =
        'recommended';

      setCustomPageMessage(
        'Az ajánlott alapoldalak visszaállítva.',
        'success'
      );

      setProgress(
        'pages'
      );

      commit({
        rerenderPages:
          true,

        updateStatus:
          true
      });
    }

    function renderDepthOptions() {
      if (
        !elements.depthGrid
      ) {
        return;
      }

      elements
        .depthGrid
        .innerHTML =
          '';

      data
        .catalogDepthOptions
        .forEach(
          (option) => {
            const selected =
              option.id ===
              state
                .catalogDepthId;

            const button =
              document.createElement(
                'button'
              );

            button.type =
              'button';

            button.className =
              `zln-structure-depth-card${
                selected
                  ? ' is-selected'
                  : ''
              }${
                option.recommended
                  ? ' is-recommended'
                  : ''
              }`;

            button.setAttribute(
              'role',
              'listitem'
            );

            button.setAttribute(
              'aria-pressed',
              selected
                ? 'true'
                : 'false'
            );

            const pathMarkup =
              option.path
                .map(
                  (
                    step,
                    index
                  ) => {
                    const arrow =
                      index <
                      option.path
                        .length -
                        1
                        ? `
                          <span class="zln-structure-depth-card__path-arrow">
                            →
                          </span>
                        `
                        : '';

                    return `
                      <span class="zln-structure-depth-card__path-step">
                        ${escapeHtml(
                          step
                        )}
                      </span>

                      ${arrow}
                    `;
                  }
                )
                .join('');

            button.innerHTML = `
              <span class="zln-structure-depth-card__top">
                <span class="zln-structure-depth-card__level">
                  ${String(
                    option.level
                  ).padStart(
                    2,
                    '0'
                  )}
                </span>

                <span class="zln-structure-depth-card__top-labels">
                  ${
                    option.recommended
                      ? `
                        <span class="zln-structure-depth-card__badge">
                          ${escapeHtml(
                            option.badge ||
                            'Ajánlott'
                          )}
                        </span>
                      `
                      : ''
                  }

                  <span class="zln-structure-depth-card__selected">
                    Kiválasztva
                  </span>
                </span>
              </span>

              <span class="zln-structure-depth-card__name">
                ${escapeHtml(
                  option.name
                )}
              </span>

              <span class="zln-structure-depth-card__description">
                ${escapeHtml(
                  option.description
                )}
              </span>

              <span class="zln-structure-depth-card__path">
                ${pathMarkup}
              </span>

              <span class="zln-structure-depth-card__recommended">
                <strong>Ehhez ajánlott:</strong>
                ${escapeHtml(
                  option.recommendedFor
                )}
              </span>
            `;

            button.addEventListener(
              'click',
              () => {
                state
                  .catalogDepthId =
                    option.id;

                state
                  .structureMode =
                    'customized';

                setProgress(
                  'catalog'
                );

                commit({
                  rerenderDepth:
                    true,

                  rerenderCatalogFields:
                    true,

                  updateStatus:
                    true
                });
              }
            );

            elements
              .depthGrid
              .appendChild(
                button
              );
          }
        );
    }

    function renderCatalogPath() {
      const depth =
        getDepth();

      if (
        elements
          .catalogPathName
      ) {
        elements
          .catalogPathName
          .textContent =
            depth.name;
      }

      if (
        elements
          .catalogPathSteps
      ) {
        elements
          .catalogPathSteps
          .innerHTML =
            depth.path
              .map(
                (
                  step,
                  index
                ) => {
                  const arrow =
                    index <
                    depth.path
                      .length -
                      1
                      ? `
                        <span class="zln-structure-catalog-path__arrow">
                          →
                        </span>
                      `
                      : '';

                  return `
                    <span class="zln-structure-catalog-path__step">
                      ${escapeHtml(
                        step
                      )}
                    </span>

                    ${arrow}
                  `;
                }
              )
              .join('');
      }
    }

    function renderCatalogDetails() {
      const depth =
        getDepth();

      renderCatalogPath();

      const visibleFields =
        data.catalogFields.filter(
          (field) => {
            return (
              depth.level >=
              field
                .visibleFromLevel
            );
          }
        );

      if (
        elements
          .catalogFields
      ) {
        elements
          .catalogFields
          .hidden =
            visibleFields
              .length ===
            0;
      }

      if (
        !elements
          .catalogFieldGrid
      ) {
        return;
      }

      elements
        .catalogFieldGrid
        .innerHTML =
          '';

      visibleFields.forEach(
        (field) => {
          const wrapper =
            document.createElement(
              'label'
            );

          wrapper.className =
            'zln-structure-catalog-field';

          wrapper.innerHTML = `
            <span class="zln-structure-catalog-field__copy">
              <span class="zln-structure-catalog-field__name">
                ${escapeHtml(
                  field.name
                )}
              </span>

              <span class="zln-structure-catalog-field__description">
                ${escapeHtml(
                  field.description
                )}
              </span>
            </span>

            <input
              class="zln-structure-catalog-field__input"
              type="number"
              min="${field.minimum}"
              max="${field.maximum}"
              step="1"
              value="${state.catalog[
                field.id
              ]}"
              inputmode="numeric"
              data-structure-catalog-field="${escapeHtml(
                field.id
              )}"
            >
          `;

          const input =
            query(
              wrapper,
              '[data-structure-catalog-field]'
            );

          input
            ?.addEventListener(
              'input',
              () => {
                state.catalog[
                  field.id
                ] =
                  clamp(
                    toInteger(
                      input.value,
                      field.minimum
                    ),
                    field.minimum,
                    field.maximum
                  );

                state
                  .structureMode =
                    'customized';

                setProgress(
                  'catalog'
                );

                commit({
                  updateStatus:
                    true
                });
              }
            );

          input
            ?.addEventListener(
              'blur',
              () => {
                input.value =
                  String(
                    state.catalog[
                      field.id
                    ]
                  );
              }
            );

          elements
            .catalogFieldGrid
            .appendChild(
              wrapper
            );
        }
      );
    }

    function setProductCount(
      value
    ) {
      if (
        !state
          .uploadService
      ) {
        return;
      }

      state.productCount =
        clamp(
          toInteger(
            value,
            0
          ),
          data
            .productOptions
            .productCount
            .minimum,
          data
            .productOptions
            .productCount
            .maximum
        );

      state.structureMode =
        'customized';

      setProgress(
        'products'
      );

      commit({
        updateStatus:
          true
      });
    }

    function renderProductControls() {
      const calculations =
        calculateStructure(
          state,
          data
        );

      const enabled =
        Boolean(
          state
            .uploadService
        );

      if (
        !enabled &&
        data
          .productOptions
          .productCount
          .resetWhenUploadDisabled !==
          false
      ) {
        state.productCount =
          0;
      }

      if (
        elements
          .productCountCard
      ) {
        elements
          .productCountCard
          .classList
          .toggle(
            'is-disabled',
            !enabled
          );

        elements
          .productCountCard
          .setAttribute(
            'aria-disabled',
            enabled
              ? 'false'
              : 'true'
          );
      }

      if (
        elements
          .productInput
      ) {
        elements
          .productInput
          .value =
            String(
              calculations
                .productCount
            );

        elements
          .productInput
          .disabled =
            !enabled;
      }

      if (
        elements
          .productDecrease
      ) {
        elements
          .productDecrease
          .disabled =
            !enabled ||
            calculations
              .productCount <=
              data
                .productOptions
                .productCount
                .minimum;
      }

      if (
        elements
          .productIncrease
      ) {
        elements
          .productIncrease
          .disabled =
            !enabled ||
            calculations
              .productCount >=
              data
                .productOptions
                .productCount
                .maximum;
      }

      if (
        elements
          .dynamicProductPages
      ) {
        elements
          .dynamicProductPages
          .textContent =
            pluralizeProducts(
              calculations
                .dynamicProductPages
            );
      }

      if (
        elements
          .uploadInput
      ) {
        elements
          .uploadInput
          .checked =
            enabled;
      }

      if (
        elements
          .uploadLabel
      ) {
        elements
          .uploadLabel
          .textContent =
            enabled
              ? (
                  data
                    .productOptions
                    .uploadService
                    .enabledLabel ||
                  'Igen, kérem'
                )
              : (
                  data
                    .productOptions
                    .uploadService
                    .disabledLabel ||
                  'Nem kérem'
                );
      }
    }

    function renderNotes() {
      if (
        elements.notes
      ) {
        elements
          .notes
          .value =
            state.notes;
      }

      if (
        elements
          .notesCount
      ) {
        elements
          .notesCount
          .textContent =
            `${state.notes.length} / 2000`;
      }
    }

    function renderSummary() {
      const calculations =
        calculateStructure(
          state,
          data
        );

      if (
        elements
          .standardPageCount
      ) {
        elements
          .standardPageCount
          .textContent =
            formatNumber(
              calculations
                .standardPages
            );
      }

      if (
        elements
          .customPageCountSummary
      ) {
        elements
          .customPageCountSummary
          .textContent =
            formatNumber(
              calculations
                .customPages
            );
      }

      if (
        elements
          .uniquePages
      ) {
        elements
          .uniquePages
          .textContent =
            formatNumber(
              calculations
                .uniquePages
            );
      }

      if (
        elements
          .standardPageTotal
      ) {
        elements
          .standardPageTotal
          .textContent =
            pluralizePages(
              calculations
                .standardPages
            );
      }

      if (
        elements
          .pageTotal
      ) {
        elements
          .pageTotal
          .textContent =
            pluralizePages(
              calculations
                .uniquePages
            );
      }

      if (
        elements
          .catalogDepthName
      ) {
        elements
          .catalogDepthName
          .textContent =
            calculations
              .depth
              .name;
      }

      if (
        elements
          .productCountSummary
      ) {
        elements
          .productCountSummary
          .textContent =
            formatNumber(
              calculations
                .productCount
            );
      }

      if (
        elements
          .uploadState
      ) {
        elements
          .uploadState
          .textContent =
            calculations
              .uploadRequested
              ? 'Kért'
              : 'Nem kért';
      }

      if (
        elements
          .customPageCount
      ) {
        elements
          .customPageCount
          .textContent =
            pluralizePages(
              calculations
                .customPages
            );
      }
    }

    function renderStatus() {
      const calculations =
        calculateStructure(
          state,
          data
        );

      const hasPages =
        calculations
          .uniquePages >=
        1;

      const uploadComplete =
        !calculations
          .uploadRequested ||
        calculations
          .productCount >=
        1;

      const complete =
        hasPages &&
        uploadComplete;

      if (
        elements.status
      ) {
        if (
          !hasPages
        ) {
          elements
            .status
            .textContent =
              'A struktúrában legalább egy oldalnak szerepelnie kell.';
        } else if (
          calculations
            .uploadRequested &&
          calculations
            .productCount <
            1
        ) {
          elements
            .status
            .textContent =
              'Add meg, hány terméket töltsünk fel.';
        } else {
          elements
            .status
            .textContent =
              `${formatNumber(
                calculations
                  .standardPages
              )} alapoldal, ${formatNumber(
                calculations
                  .customPages
              )} saját oldal és ${calculations
                .depth
                .shortName
                .toLowerCase()} katalógus elmentve.`;
        }
      }

      elements
        .statusDot
        ?.classList
        .toggle(
          'is-complete',
          complete
        );

      if (
        elements
          .nextButton
      ) {
        elements
          .nextButton
          .disabled =
            !complete;
      }
    }

    function renderAll() {
      renderPageGroups();
      renderCustomPages();
      renderDepthOptions();
      renderCatalogDetails();
      renderProductControls();
      renderNotes();
      renderSummary();
      renderStatus();
    }

    function navigateToStep(
      step
    ) {
      if (
        !section
      ) {
        return false;
      }

      const stepButton =
        query(
          section,
          `[data-labor-step-button="${step}"]`
        );

      if (
        stepButton &&
        !stepButton.disabled
      ) {
        stepButton.click();
        return true;
      }

      const targetStage =
        query(
          section,
          `[data-labor-stage="${step}"]`
        );

      if (
        !targetStage
      ) {
        return false;
      }

      queryAll(
        section,
        '[data-labor-stage]'
      ).forEach(
        (
          laborStage
        ) => {
          laborStage.hidden =
            laborStage !==
            targetStage;
        }
      );

      const latestMainState =
        readStorage(
          MAIN_STORAGE_KEY,
          {}
        );

      writeStorage(
        MAIN_STORAGE_KEY,
        {
          ...latestMainState,

          currentStep:
            step,

          updatedAt:
            new Date()
              .toISOString()
        }
      );

      document.dispatchEvent(
        new CustomEvent(
          'zln:labor-step-changed',
          {
            detail: {
              step
            }
          }
        )
      );

      return true;
    }

    function bindRecommendedReset() {
      elements
        .resetRecommended
        ?.addEventListener(
          'click',
          resetRecommendedStructure
        );
    }

    function bindCustomPages() {
      elements
        .customPageAdd
        ?.addEventListener(
          'click',
          addCustomPage
        );

      elements
        .customPageName
        ?.addEventListener(
          'keydown',
          (
            event
          ) => {
            if (
              event.key !==
                'Enter' ||
              event.shiftKey
            ) {
              return;
            }

            event.preventDefault();

            addCustomPage();
          }
        );
    }

    function bindProductControls() {
      elements
        .productDecrease
        ?.addEventListener(
          'click',
          () => {
            setProductCount(
              state
                .productCount -
              1
            );
          }
        );

      elements
        .productIncrease
        ?.addEventListener(
          'click',
          () => {
            setProductCount(
              state
                .productCount +
              1
            );
          }
        );

      elements
        .productInput
        ?.addEventListener(
          'input',
          () => {
            setProductCount(
              elements
                .productInput
                .value
            );
          }
        );

      elements
        .productInput
        ?.addEventListener(
          'blur',
          () => {
            elements
              .productInput
              .value =
                String(
                  state
                    .productCount
                );
          }
        );

      elements
        .uploadInput
        ?.addEventListener(
          'change',
          () => {
            state
              .uploadService =
                elements
                  .uploadInput
                  .checked;

            if (
              !state
                .uploadService &&
              data
                .productOptions
                .productCount
                .resetWhenUploadDisabled !==
                false
            ) {
              state
                .productCount =
                  0;
            }

            state
              .structureMode =
                'customized';

            setProgress(
              'products'
            );

            commit({
              updateStatus:
                true
            });

            if (
              state
                .uploadService
            ) {
              window
                .requestAnimationFrame(
                  () => {
                    elements
                      .productInput
                      ?.focus();
                  }
                );
            }
          }
        );
    }

    function bindNotes() {
      elements
        .notes
        ?.addEventListener(
          'input',
          () => {
            state.notes =
              String(
                elements
                  .notes
                  .value ||
                ''
              ).slice(
                0,
                2000
              );

            if (
              elements
                .notesCount
            ) {
              elements
                .notesCount
                .textContent =
                  `${state.notes.length} / 2000`;
            }

            scheduleNotesSave();
          }
        );
    }

    function bindNavigation() {
      elements
        .backButton
        ?.addEventListener(
          'click',
          () => {
            saveState(
              state,
              data
            );

            navigateToStep(
              'typography'
            );
          }
        );

      elements
        .nextButton
        ?.addEventListener(
          'click',
          () => {
            const calculations =
              calculateStructure(
                state,
                data
              );

            const complete =
              calculations
                .uniquePages >=
                1 &&
              (
                !calculations
                  .uploadRequested ||
                calculations
                  .productCount >=
                  1
              );

            if (
              !complete
            ) {
              renderStatus();
              return;
            }

            saveState(
              state,
              data
            );

            dispatchStructureUpdate();

            const navigated =
              navigateToStep(
                'summary'
              );

            if (
              !navigated &&
              elements.status
            ) {
              elements
                .status
                .textContent =
                  'A struktúra elmentve. Az Összesítő bekötése lesz a következő lépés.';
            }
          }
        );
    }

    function bindProgressTracking() {
      queryAll(
        stage,
        '[data-structure-section]'
      ).forEach(
        (panel) => {
          const panelId =
            panel.dataset
              .structureSection;

          const progressId =
            [
              'pages',
              'custom',
              'catalog',
              'products'
            ].includes(
              panelId
            )
              ? panelId
              : null;

          if (
            !progressId
          ) {
            return;
          }

          [
            'focusin',
            'pointerdown'
          ].forEach(
            (eventName) => {
              panel.addEventListener(
                eventName,
                () => {
                  setProgress(
                    progressId
                  );
                }
              );
            }
          );
        }
      );
    }

    function refreshFromStorage() {
      const refreshed =
        loadState(
          data
        );

      Object.keys(
        state
      ).forEach(
        (key) => {
          delete state[
            key
          ];
        }
      );

      Object.assign(
        state,
        refreshed
      );

      renderAll();
    }

    function bindVisibilityRefresh() {
      const refreshWhenVisible =
        () => {
          if (
            stage.hidden
          ) {
            return;
          }

          refreshFromStorage();
        };

      const observer =
        new MutationObserver(
          (
            mutations
          ) => {
            const visibilityChanged =
              mutations.some(
                (
                  mutation
                ) => {
                  return (
                    mutation.type ===
                      'attributes' &&
                    mutation
                      .attributeName ===
                      'hidden'
                  );
                }
              );

            if (
              visibilityChanged &&
              !stage.hidden
            ) {
              refreshFromStorage();
            }
          }
        );

      observer.observe(
        stage,
        {
          attributes:
            true,

          attributeFilter: [
            'hidden'
          ]
        }
      );

      document.addEventListener(
        'zln:labor-step-changed',
        (
          event
        ) => {
          if (
            event.detail
              ?.step !==
            'structure'
          ) {
            return;
          }

          window
            .requestAnimationFrame(
              refreshWhenVisible
            );
        }
      );

      window.addEventListener(
        'storage',
        (
          event
        ) => {
          if (
            event.key !==
              MAIN_STORAGE_KEY &&
            event.key !==
              STRUCTURE_STORAGE_KEY
          ) {
            return;
          }

          refreshWhenVisible();
        }
      );
    }

    bindRecommendedReset();
    bindCustomPages();
    bindProductControls();
    bindNotes();
    bindNavigation();
    bindProgressTracking();
    bindVisibilityRefresh();

    setProgress(
      'pages'
    );

    renderAll();

    saveState(
      state,
      data
    );
  }

  function initializeAll() {
    const data =
      window
        .ZLN_LABOR_STRUCTURE_DATA;

    if (
      !data ||
      !Array.isArray(
        data.pageGroups
      ) ||
      !Array.isArray(
        data.pageTypeOptions
      ) ||
      !Array.isArray(
        data.catalogDepthOptions
      ) ||
      !Array.isArray(
        data.catalogFields
      )
    ) {
      return;
    }

    document
      .querySelectorAll(
        '[data-zln-structure-labor]'
      )
      .forEach(
        (stage) => {
          initializeLabor(
            stage,
            data
          );
        }
      );
  }

  document.addEventListener(
    'zln:labor-structure-data-ready',
    initializeAll
  );

  document.addEventListener(
    'shopify:section:load',
    (
      event
    ) => {
      const data =
        window
          .ZLN_LABOR_STRUCTURE_DATA;

      if (
        !data
      ) {
        ensureDataFile();
        return;
      }

      event.target
        .querySelectorAll(
          '[data-zln-structure-labor]'
        )
        .forEach(
          (stage) => {
            initializeLabor(
              stage,
              data
            );
          }
        );
    }
  );

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      ensureDataFile,
      {
        once: true
      }
    );
  } else {
    ensureDataFile();
  }
})();